/**
 * StoneTablet — chisel-style narration that reveals text one character
 * at a time. Apr 29 2026 Lead's call: "Ive said this 5 times that text
 * should not have a background." The sandstone tablet surface is gone;
 * the chiseled prose now appears directly on the page background. Kept:
 * the chisel reveal animation, serif typography, ink shadow, the chisel
 * SFX hammer-tap, the gold cursor.
 */
import { useEffect, useState, useRef } from 'react';

export default function StoneTablet({
  text = '',
  speedCps = 22,
  autoStart = true,
  pauseTokens = ['[pause]'],
  onComplete,
  testId = 'stone-tablet',
}) {
  const [chars, setChars] = useState(0);
  const [done, setDone] = useState(false);
  const startedAt = useRef(0);
  const rafRef = useRef(null);
  const completedRef = useRef(false);

  // Apr 29 2026 follow-up #2: chisel hammer-tap SFX disabled by Lead's
  // call ("The sound of the text being written out need to be removed").
  // The reveal animation (gold cursor + character-by-character ink) is
  // preserved; only the per-character audio is muted. The audio hook is
  // still imported so the rest of the app keeps the SFX preference
  // toggle working for other components (button clicks, etc.).
  const playTap = () => {};
  const lastTappedCharRef = useRef(0);

  const cleanText = text.replace(/\[pause\]/gi, ' ').replace(/\s{2,}/g, ' ').trim();

  useEffect(() => {
    if (!autoStart || !cleanText) return;
    startedAt.current = performance.now();
    const tick = (now) => {
      const elapsed = (now - startedAt.current) / 1000;
      const target = Math.min(cleanText.length, Math.floor(elapsed * speedCps));
      if (target > lastTappedCharRef.current) {
        let taps = 0;
        for (let i = lastTappedCharRef.current; i < target && taps < 2; i++) {
          const ch = cleanText[i];
          if (ch && /\S/.test(ch)) { playTap(); taps += 1; }
        }
        lastTappedCharRef.current = target;
      }
      setChars(target);
      if (target < cleanText.length) {
        rafRef.current = requestAnimationFrame(tick);
      } else if (!completedRef.current) {
        completedRef.current = true;
        setDone(true);
        onComplete?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cleanText, autoStart, speedCps, onComplete, playTap]);

  void pauseTokens; // v1: pause tokens stripped, not honored as time holds

  const settled = cleanText.slice(0, Math.max(0, chars - 1));
  const fresh = chars > 0 ? cleanText[chars - 1] : '';
  const showCursor = !done && chars < cleanText.length;

  return (
    <div data-testid={testId} className="relative">
      <p
        className="text-white/85 leading-relaxed"
        style={{
          fontFamily: '"EB Garamond", "Old Standard TT", Georgia, serif',
          fontSize: 'clamp(16px, 1.8vw, 24px)',
          letterSpacing: '0.015em',
          // Subtle ink shadow keeps the chiseled feel without a stone surface.
          textShadow: '0 1px 0 rgba(0,0,0,0.7), 0 0 16px rgba(0,0,0,0.4)',
          minHeight: '5em',
        }}
        data-testid={`${testId}-text`}
      >
        <span>{settled}</span>
        {fresh && (
          <span
            className="chisel-fresh"
            style={{
              color: '#fef3c7',
              textShadow: '0 0 14px rgba(251,191,36,0.85), 1px 1px 0 rgba(0,0,0,0.8)',
              transition: 'color 0.18s ease-out',
            }}
          >{fresh}</span>
        )}
        {/* Typewriter cursor REMOVED Apr 30 2026 per Lead — the gold
            `I|` pulsing strip below the listen button was perceived as
            a stuck text-input caret on every chain page. The chisel
            reveal animation (settled + fresh spans) carries enough
            forward motion on its own. */}
      </p>
    </div>
  );
}
